import { Device } from "@capacitor/device";
import { PushNotifications } from "@capacitor/push-notifications";
import { Capacitor } from "@capacitor/core";
import { getAPIClient } from "./apiClient";
import { errorLog } from "./utils/logger";
import Bugsnag from "@bugsnag/js";
import { updateDevicesAtom } from "./jotai/atomUpdater";
import tokenSupplier from "./docket-apis/tokens/token-supplier";

export const registerPushPermission = async () => {
  if (!Capacitor.isNativePlatform()) return;
  let permStatus = await PushNotifications.checkPermissions();

  if (permStatus.receive === "prompt") {
    permStatus = await PushNotifications.requestPermissions();
  }

  if (permStatus.receive === "granted") {
    await PushNotifications.register();
  }

  return permStatus.receive === "granted";
};

/**
 * Re-assert this device's push registration on app start.
 *
 * `registerPushPermission` only runs from the login and sign up screens, so a returning user whose session is restored
 * from storage never re-registers. That matters most on Android: FCM delivers a rotated token to
 * `PushNotificationsPlugin.onNewToken`, which drops it when the plugin instance is null — i.e. whenever the token
 * rotates while the app is not running. Nothing would re-register it afterwards, so the backend keeps pushing to a
 * dead device ID until the user next signs in. Apple similarly expects `registerForRemoteNotifications` on each launch.
 *
 * This deliberately never prompts. It registers only when permission is already granted, so a user who declined, or
 * who has not been asked yet, does not get the OS dialog thrown at them on startup. Attach the registration listener
 * before calling this, since registering is what triggers it.
 */
export const refreshPushRegistration = async () => {
  if (!Capacitor.isNativePlatform()) return;

  try {
    if (!(await tokenSupplier())) return;

    const permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive !== "granted") return;

    await PushNotifications.register();
  } catch (error) {
    /**
     * This runs during app bootstrap, so it must never reject: a failure to refresh the push registration is not a
     * reason to stop the app from starting.
     */
    errorLog(error as Error);
    Bugsnag.notify(error as Error);
  }
};

export const initializePushNotificationListeners = async () => {
  if (!Capacitor.isNativePlatform()) return;

  await PushNotifications.addListener("registration", (token) => {
    // Need to explicitly tell Typescript that we are not handling the returned promise
    // since the event listener expects a synchronous function
    void (async () => {
      try {
        /**
         * This listener is attached at app startup and is never detached, so a registration event can arrive while
         * nobody is logged in: before the first sign in, or any time after a logout cleared the stored tokens. On
         * Android in particular, FCM fires `onNewToken` independently of our `register()` call (token rotation,
         * reinstall, app data restore) and Capacitor retains that event until a listener consumes it, so it can be
         * delivered the moment we attach below. Calling the API without tokens just throws, so bail out instead.
         * Nothing is lost by dropping the token here: `registerPushPermission` re-registers on the next login, which
         * makes both platforms re-deliver the current token.
         */
        if (!(await tokenSupplier())) {
          return;
        }
        const device = await Device.getInfo();
        const apiClient = getAPIClient();
        const devices = await apiClient.getDevices();
        /**
         * Casing of device ID is not guaranteed so compare in a case-insensitive manner.
         */
        const existingDevice = devices.find(
          (device) => device.device_id.toLowerCase() === token.value.toLowerCase()
        );

        if (!existingDevice) {
          const fallbackDeviceName =
            Capacitor.getPlatform() === "ios" ? "iPhone Device" : "Android Device";
          await apiClient.createDevice({
            device_id: token.value,
            // Note on ios 16+ we should just get a generic device name since Apple denied our request for
            // https://developer.apple.com/documentation/bundleresources/entitlements/com.apple.developer.device-information.user-assigned-device-name
            name: device.name || fallbackDeviceName,
            platform: device.platform,
          });
        }
        await updateDevicesAtom();
      } catch (error) {
        errorLog(error as Error);
        Bugsnag.notify(error as Error);
      }
    })();
  });

  await PushNotifications.addListener("registrationError", (error) => {
    errorLog(new Error(error.error));
    Bugsnag.notify(new Error(error.error));
  });
};
